import React, { useEffect, useState } from "react";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import Loader from "../../common/Loader";
import { CallingV2 } from "../../utils/APIClient";
import { triggerAlert } from "../../utils";

/**
 * Calling V2 → AI Call Summary configuration.
 *
 * Global singleton controlling the OpenAI-backed post-call summarizer.
 * The `generate_call_summaries` cron picks up completed calls with a
 * transcript and calls OpenAI using these fields.
 *
 * The API key is redacted on GET (last 4 chars) so the value is never
 * revealed to page-source scrapers. Submitting with an unchanged
 * redacted-looking string is a no-op on the backend.
 */
export default function CallingV2SummaryConfig() {
  document.title = "Digital Employees — Call Summary";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [config, setConfig] = useState({
    is_enabled: false,
    openai_api_key_is_set: false,   // reflects OPENAI_CALL_SUMMARIZER_KEY env var
    model: "gpt-4o-mini",
    system_prompt: "",
    temperature: "0.30",
    max_tokens: 500,
    updated_at: null,
    updated_by_email: "",
  });

  // Test panel
  const [testTranscript, setTestTranscript] = useState(
    "Agent: Hi, thanks for calling Acme. This is Sarah, how can I help?\n" +
    "Caller: I'm looking for pricing on the enterprise plan.\n" +
    "Agent: Great — the enterprise plan starts at $500/month. Would you like me to email you the full breakdown?\n" +
    "Caller: Yes please, my email is jane@example.com.\n" +
    "Agent: Perfect, I'll send that right over. Anything else?\n" +
    "Caller: No, that's it. Thanks!"
  );
  const [testResult, setTestResult] = useState("");
  const [testError, setTestError] = useState("");

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const r = await CallingV2.getSummaryConfig();
      setConfig(r.data);
    } catch (e) {
      triggerAlert("error", "Oops...", "Failed to load summary config.");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchConfig(); }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      is_enabled: config.is_enabled,
      model: config.model,
      system_prompt: config.system_prompt,
      temperature: parseFloat(config.temperature),
      max_tokens: parseInt(config.max_tokens, 10),
    };
    // API key is not sent — server reads OPENAI_CALL_SUMMARIZER_KEY from .env.
    try {
      const r = await CallingV2.updateSummaryConfig(payload);
      setConfig(r.data);
      triggerAlert("success", "Saved", "Summary config updated.");
    } catch (e) {
      const msg = e?.response?.data?.message || "Save failed.";
      triggerAlert("error", "Oops...", msg);
    } finally { setSaving(false); }
  };

  const onTest = async () => {
    if (!testTranscript.trim()) {
      triggerAlert("error", "Oops...", "Paste a sample transcript first.");
      return;
    }
    setTesting(true);
    setTestResult("");
    setTestError("");
    try {
      const r = await CallingV2.testSummaryConfig(testTranscript);
      setTestResult(r.data.summary || "");
    } catch (e) {
      setTestError(e?.response?.data?.message || "Test failed.");
    } finally { setTesting(false); }
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <PageHeader mainHeading="Call Summary" heading="Calling V2 / Call Summary" />
            {loading ? <Loader /> : (
              <div className="row">
                <div className="col-lg-8">
                  <div className="card">
                    <div className="card-body">
                      <h4 className="card-title mb-1">Post-call AI summarization</h4>
                      <p className="text-muted mb-4">
                        When enabled, every completed call is summarized by OpenAI
                        1–2 minutes after it ends. Customers see the summary at
                        the top of the Call History drawer, above the transcript.
                      </p>

                      <form onSubmit={onSave}>
                        {/* API-key status banner. Key comes from the .env — admin can't edit it here. */}
                        <div className={`alert ${config.openai_api_key_is_set ? 'alert-success' : 'alert-warning'} d-flex align-items-center mb-4`}>
                          <i className={`bx ${config.openai_api_key_is_set ? 'bx-check-circle' : 'bx-error-circle'} me-2`} style={{ fontSize: 20 }}></i>
                          <div>
                            {config.openai_api_key_is_set ? (
                              <>OpenAI API key is configured (from <code>OPENAI_CALL_SUMMARIZER_KEY</code> in the server .env).</>
                            ) : (
                              <><strong>Not configured.</strong> Ask ops to add <code>OPENAI_CALL_SUMMARIZER_KEY=sk-...</code> to the server's <code>.env</code> and restart Django.</>
                            )}
                          </div>
                        </div>

                        {/* Enable toggle */}
                        <div className="form-check form-switch form-switch-lg mb-4">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isEnabled"
                            checked={!!config.is_enabled}
                            disabled={!config.openai_api_key_is_set}
                            onChange={(e) => setConfig({ ...config, is_enabled: e.target.checked })}
                          />
                          <label className="form-check-label" htmlFor="isEnabled">
                            <strong>Enable AI call summaries</strong>
                            <div className="text-muted small">
                              When off, no summaries are generated and pending
                              calls stay queued until you turn it back on.
                              {!config.openai_api_key_is_set && ' Disabled until the env key is set.'}
                            </div>
                          </label>
                        </div>

                        {/* Model */}
                        <div className="mb-3">
                          <label className="form-label">Model</label>
                          <select
                            className="form-select"
                            value={config.model}
                            onChange={(e) => setConfig({ ...config, model: e.target.value })}
                          >
                            <option value="gpt-4o-mini">gpt-4o-mini — cheapest, fast, good enough</option>
                            <option value="gpt-4o">gpt-4o — highest quality</option>
                            <option value="gpt-4.1-mini">gpt-4.1-mini — new small model</option>
                            <option value="gpt-3.5-turbo">gpt-3.5-turbo — legacy, cheap</option>
                          </select>
                        </div>

                        {/* System prompt */}
                        <div className="mb-3">
                          <label className="form-label">System prompt</label>
                          <textarea
                            className="form-control"
                            rows={9}
                            value={config.system_prompt}
                            onChange={(e) => setConfig({ ...config, system_prompt: e.target.value })}
                            style={{ fontFamily: 'monospace', fontSize: 13 }}
                          />
                          <div className="form-text">
                            Describe the shape, tone and length you want. Sent
                            as the <code>system</code> message before the
                            transcript.
                          </div>
                        </div>

                        {/* Temperature + max_tokens side by side */}
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Temperature</label>
                            <input
                              type="number"
                              className="form-control"
                              min="0" max="2" step="0.1"
                              value={config.temperature}
                              onChange={(e) => setConfig({ ...config, temperature: e.target.value })}
                            />
                            <div className="form-text">
                              0.0 = deterministic, 1.0 = creative. Summaries want 0.2–0.4.
                            </div>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Max tokens</label>
                            <input
                              type="number"
                              className="form-control"
                              min="50" max="4000" step="50"
                              value={config.max_tokens}
                              onChange={(e) => setConfig({ ...config, max_tokens: e.target.value })}
                            />
                            <div className="form-text">
                              500 fits a 5-bullet summary. Higher = longer output = higher cost.
                            </div>
                          </div>
                        </div>

                        {/* Save */}
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="text-muted small">
                            {config.updated_at
                              ? `Last saved ${new Date(config.updated_at).toLocaleString()}${config.updated_by_email ? ` by ${config.updated_by_email}` : ''}`
                              : ''}
                          </div>
                          <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : 'Save changes'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>

                {/* Test panel */}
                <div className="col-lg-4">
                  <div className="card">
                    <div className="card-body">
                      <h4 className="card-title mb-1">Test</h4>
                      <p className="text-muted small">
                        Runs your saved config against the sample transcript
                        below so you can iterate on the system prompt before
                        turning the feature on for real calls.
                      </p>
                      <div className="mb-3">
                        <label className="form-label">Sample transcript</label>
                        <textarea
                          className="form-control"
                          rows={10}
                          value={testTranscript}
                          onChange={(e) => setTestTranscript(e.target.value)}
                          style={{ fontFamily: 'monospace', fontSize: 12 }}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-primary w-100 mb-3"
                        onClick={onTest}
                        disabled={testing || !config.openai_api_key_is_set}
                      >
                        {testing ? 'Running...' : 'Run test summary'}
                      </button>
                      {!config.openai_api_key_is_set && (
                        <div className="alert alert-info small py-2">
                          Set <code>OPENAI_CALL_SUMMARIZER_KEY</code> in the
                          server .env first — then Test.
                        </div>
                      )}
                      {testError && (
                        <div className="alert alert-warning small py-2" style={{ whiteSpace: 'pre-wrap' }}>
                          {testError}
                        </div>
                      )}
                      {testResult && (
                        <>
                          <label className="form-label small text-muted text-uppercase">Result</label>
                          <div
                            className="p-3 border rounded bg-light"
                            style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6 }}
                          >
                            {testResult}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
